import { useAuthContext } from '@/auth/useAuthContext';
import { UndoBanner } from '@/components/UndoBanner';
import { useBookingRequestCount } from '@/lib/useBookingRequestCount';
import { cn } from '@/lib/utils';
import {
  Activity,
  AlertTriangle,
  BarChart3,
  Calendar as CalendarIcon,
  History as HistoryIcon,
  Hourglass,
  LayoutDashboard,
  ListChecks,
  LogOut,
  Mail,
  Megaphone,
  Menu,
  PanelLeft,
  PawPrint,
  Plus,
  RotateCcw,
  Settings,
  ShieldAlert,
  ShoppingCart,
  Sparkles,
  Stethoscope,
  Syringe,
  Truck,
  Users,
  X,
} from 'lucide-react';
import { useEffect, useState } from 'react';
import { Link, NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { FacilityPicker } from './FacilityPicker';
import { LiveIndicator } from './LiveIndicator';
import { WhatsNewButton } from './WhatsNewButton';

/** Sidebar sections, in display order. Every navigable item belongs to one. */
const NAV_GROUPS = ['Operations', 'Front desk', 'Records', 'Insights'] as const;
type NavGroup = (typeof NAV_GROUPS)[number];

interface NavItem {
  to: string;
  label: string;
  Icon: React.ComponentType<{ className?: string; strokeWidth?: number }>;
  group: NavGroup;
  /** Capability slug required to see this nav entry. Currently informational only;
   *  the page itself enforces the gate via server 403 responses. */
  capabilityRequired?: string;
}

/**
 * Primary create action. Promoted out of the nav list into a filled CTA pinned
 * at the top of the rail — it's an action, not a destination, so it shouldn't
 * sit at the same visual weight as navigation.
 */
const PRIMARY_ACTION = { to: '/bookings/new', label: 'New booking', Icon: Plus };

const ALL_NAV: NavItem[] = [
  // Operations
  { to: '/today', label: 'Today', Icon: LayoutDashboard, group: 'Operations' },
  { to: '/health', label: 'Health Monitor', Icon: Activity, group: 'Operations' },
  { to: '/tasks', label: 'Schedule', Icon: ListChecks, group: 'Operations' },
  { to: '/calendar', label: 'Calendar', Icon: CalendarIcon, group: 'Operations' },
  {
    to: '/transport',
    label: 'Transport',
    Icon: Truck,
    group: 'Operations',
    capabilityRequired: 'transport.view',
  },
  // Front desk
  {
    to: '/pos',
    label: 'POS',
    Icon: ShoppingCart,
    group: 'Front desk',
    capabilityRequired: 'pos.session.read',
  },
  { to: '/customers', label: 'Customers', Icon: Users, group: 'Front desk' },
  {
    to: '/vets',
    label: 'Vets',
    Icon: Stethoscope,
    group: 'Front desk',
    capabilityRequired: 'vets.read',
  },
  // Records
  { to: '/history', label: 'History', Icon: HistoryIcon, group: 'Records' },
  { to: '/waitlist', label: 'Waitlist', Icon: Hourglass, group: 'Records' },
  {
    to: '/refund-requests',
    label: 'Refunds',
    Icon: RotateCcw,
    group: 'Records',
    capabilityRequired: 'payments.refund',
  },
  {
    to: '/vaccine-overrides',
    label: 'Vaccine overrides',
    Icon: Syringe,
    group: 'Records',
    capabilityRequired: 'vaccines.configure',
  },
  // Insights
  {
    to: '/reports',
    label: 'Reports',
    Icon: BarChart3,
    group: 'Insights',
    capabilityRequired: 'reports.view_operational',
  },
  { to: '/marketing', label: 'Marketing', Icon: Megaphone, group: 'Insights' },
  { to: '/communications/inbox', label: 'Communications', Icon: Mail, group: 'Insights' },
  {
    to: '/incidents',
    label: 'Incidents',
    Icon: ShieldAlert,
    group: 'Insights',
    capabilityRequired: 'incidents.read',
  },
  {
    to: '/copilot',
    label: 'AI Copilot',
    Icon: Sparkles,
    group: 'Insights',
    capabilityRequired: 'copilot.use',
  },
];

const FOOTER_NAV = [{ to: '/settings', label: 'Settings', Icon: Settings }];

/**
 * Returns the nav items the current user is allowed to see.
 * Items with no `capabilityRequired` are shown to all authenticated staff.
 * Items with a `capabilityRequired` are hidden when the active role's
 * capabilities (resolved from /api/me) don't include the required slug.
 * The server enforces the same gates — this is a UX layer only.
 */
function useNav(): NavItem[] {
  const { state } = useAuthContext();
  const capabilities: readonly string[] = state.me?.capabilities ?? [];
  const hasAll = capabilities.includes('*'); // owner wildcard — grants every nav item
  return ALL_NAV.filter(
    (item) => !item.capabilityRequired || hasAll || capabilities.includes(item.capabilityRequired),
  );
}

/** Buckets the (already capability-filtered) nav into sections, dropping empty ones. */
function groupNav(items: NavItem[]): { group: NavGroup; items: NavItem[] }[] {
  return NAV_GROUPS.map((group) => ({
    group,
    items: items.filter((item) => item.group === group),
  })).filter((section) => section.items.length > 0);
}

const COLLAPSE_KEY = 'kennel.nav.collapsed';

/** Reads the persisted rail-collapsed preference (defaults to expanded). */
function useCollapsed(): [boolean, (next: boolean) => void] {
  const [collapsed, setCollapsedState] = useState<boolean>(() => {
    try {
      return localStorage.getItem(COLLAPSE_KEY) === '1';
    } catch {
      return false;
    }
  });
  function setCollapsed(next: boolean) {
    setCollapsedState(next);
    try {
      localStorage.setItem(COLLAPSE_KEY, next ? '1' : '0');
    } catch {
      // localStorage may be unavailable (private browsing); accept ephemeral state.
    }
  }
  return [collapsed, setCollapsed];
}

export function AppShell() {
  const [collapsed, setCollapsed] = useCollapsed();
  return (
    <div className="min-h-[100dvh] bg-background text-foreground">
      <DesktopRail collapsed={collapsed} setCollapsed={setCollapsed} />
      <MobileTopBar />
      <main
        className={cn(
          'transition-[padding] duration-200',
          collapsed ? 'md:pl-[72px]' : 'md:pl-[236px]',
        )}
      >
        <FailedDeliveriesBanner />
        <UndoBanner />
        <Outlet />
      </main>
    </div>
  );
}

const DISMISS_KEY = 'kennel.commFailedBanner.dismissed';

/**
 * Polls GET /api/comm-failed-deliveries/count on mount. When count > 0 and the
 * user hasn't dismissed it in this session, renders an amber banner linking to
 * /communications/failures (Plan 5 page).
 *
 * Dismissal is sessionStorage-scoped — clears on tab close/reload so the
 * banner reappears for the next work session. Failures are operational issues
 * staff should be re-prompted about regularly.
 */
function FailedDeliveriesBanner() {
  const [count, setCount] = useState<number | null>(null);
  const [dismissed, setDismissed] = useState<boolean>(() => {
    try {
      return sessionStorage.getItem(DISMISS_KEY) === '1';
    } catch {
      return false;
    }
  });

  useEffect(() => {
    let cancelled = false;
    async function load() {
      try {
        const res = await fetch('/api/comm-failed-deliveries/count', {
          credentials: 'include',
        });
        if (!res.ok) return;
        const body = (await res.json()) as { count: number };
        if (!cancelled) setCount(body.count);
      } catch {
        // Silent — banner just doesn't show on transient failures.
      }
    }
    void load();
    return () => {
      cancelled = true;
    };
  }, []);

  function handleDismiss() {
    try {
      sessionStorage.setItem(DISMISS_KEY, '1');
    } catch {
      // sessionStorage may be unavailable (private browsing); accept ephemeral dismiss.
    }
    setDismissed(true);
  }

  if (dismissed) return null;
  if (count === null || count === 0) return null;

  return (
    <div
      role="status"
      aria-live="polite"
      className="flex items-center justify-between gap-3 border-b border-amber-300 bg-amber-50 px-4 py-2 text-sm text-amber-900"
    >
      <div className="flex items-center gap-2">
        <AlertTriangle className="size-4" strokeWidth={2} />
        <span>
          <span className="num font-medium">{count}</span>{' '}
          {count === 1 ? 'failed delivery needs' : 'failed deliveries need'} review.
        </span>
        <Link
          to="/communications/failures"
          className="font-medium underline underline-offset-2 hover:text-amber-950"
        >
          View
        </Link>
      </div>
      <button
        type="button"
        onClick={handleDismiss}
        aria-label="Dismiss"
        className="grid size-7 place-items-center rounded-md text-amber-900 hover:bg-amber-100"
      >
        <X className="size-4" strokeWidth={2} />
      </button>
    </div>
  );
}

/**
 * Migration 0095 — count badge for booking requests awaiting approval, shown
 * on the Today nav entry. Polls /api/bookings/requests/count; renders nothing
 * at zero or when the caller lacks `bookings.approve`.
 */
function BookingRequestsBadge({ compact }: { compact: boolean }) {
  const { state } = useAuthContext();
  const capabilities: readonly string[] = state.me?.capabilities ?? [];
  const canApprove = capabilities.includes('*') || capabilities.includes('bookings.approve');
  const count = useBookingRequestCount(canApprove);
  if (count === 0) return null;
  const label = `${count} booking request${count === 1 ? '' : 's'} awaiting approval`;
  return (
    <span
      role="status"
      aria-label={label}
      data-testid="booking-requests-badge"
      className={cn(
        'num grid place-items-center rounded-full bg-warning px-1.5 text-[10px] font-semibold leading-none text-warning-foreground',
        compact ? 'absolute -right-1 -top-1 h-4 min-w-4' : 'ml-auto h-5 min-w-5',
      )}
    >
      {count > 99 ? '99+' : count}
    </span>
  );
}

interface RailProps {
  collapsed: boolean;
  setCollapsed: (next: boolean) => void;
}

function DesktopRail({ collapsed, setCollapsed }: RailProps) {
  const { pathname } = useLocation();
  const sections = groupNav(useNav());

  return (
    <aside
      className={cn(
        'fixed inset-y-0 left-0 z-30 hidden flex-col border-r border-border bg-sidebar py-4 md:flex',
        'overflow-y-auto overflow-x-hidden scrollbar-hide transition-[width] duration-200',
        collapsed ? 'w-[72px] items-center px-3' : 'w-[236px] px-3',
      )}
    >
      {/* Brand / facility. In collapsed mode the picker drops to its compact
          icon-only form below the logo so multi-facility users keep the
          affordance — never trade function for chrome. */}
      <div
        className={cn(
          'mb-3 flex shrink-0 items-center gap-3',
          collapsed ? 'flex-col justify-center px-0' : 'h-10 px-1',
        )}
      >
        <div className="grid size-9 shrink-0 place-items-center rounded-xl bg-primary text-primary-foreground">
          <PawPrint className="size-5" strokeWidth={1.75} />
        </div>
        <FacilityPicker compact={collapsed} />
      </div>

      {/* Primary action */}
      <NavLink
        to={PRIMARY_ACTION.to}
        aria-label={PRIMARY_ACTION.label}
        className={cn(
          'group relative mb-3 flex h-[42px] w-full shrink-0 items-center gap-3 rounded-[10px] bg-primary font-semibold text-primary-foreground shadow-sm press',
          'hover:bg-primary/90',
          collapsed ? 'justify-center px-0' : 'px-3',
        )}
      >
        <PRIMARY_ACTION.Icon className="size-[18px] shrink-0" strokeWidth={2} />
        {!collapsed && (
          <span className="whitespace-nowrap text-[13.5px] tracking-tight">
            {PRIMARY_ACTION.label}
          </span>
        )}
        {collapsed && <RailTooltip label={PRIMARY_ACTION.label} />}
      </NavLink>

      <nav className="flex w-full flex-1 flex-col gap-0.5 min-h-0">
        {sections.map((section, i) => (
          <div key={section.group} className="flex flex-col gap-0.5">
            {collapsed ? (
              i > 0 && <div className="mx-3 my-2 h-px bg-border" />
            ) : (
              <p className="mb-1 mt-3.5 px-3 text-[10px] font-semibold uppercase tracking-[0.12em] text-muted-foreground first:mt-1">
                {section.group}
              </p>
            )}
            {section.items.map((item) => (
              <RailLink key={item.to} item={item} pathname={pathname} collapsed={collapsed} />
            ))}
          </div>
        ))}
      </nav>

      {/* Footer */}
      <div className="mt-3 flex w-full flex-col gap-0.5">
        <div className="mx-3 mb-1.5 h-px bg-border" />
        {collapsed ? (
          <div className="flex justify-center">
            <WhatsNewButton variant="rail" />
          </div>
        ) : (
          <WhatsNewButton variant="rail-expanded" />
        )}
        {FOOTER_NAV.map((item) => (
          <RailLink key={item.to} item={item} pathname={pathname} collapsed={collapsed} />
        ))}
        <SignOutButton collapsed={collapsed} />
        <button
          type="button"
          onClick={() => setCollapsed(!collapsed)}
          aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          className={cn(
            'group relative mt-0.5 flex h-9 shrink-0 items-center gap-3 rounded-lg text-muted-foreground press',
            'hover:bg-sidebar-accent hover:text-sidebar-accent-foreground',
            collapsed ? 'justify-center px-0' : 'px-3',
          )}
        >
          <PanelLeft className="size-[18px] shrink-0" strokeWidth={1.75} />
          {!collapsed && (
            <span className="whitespace-nowrap text-[13px] font-medium">Collapse</span>
          )}
          {collapsed && <RailTooltip label="Expand sidebar" />}
        </button>
        <div className={cn('mt-1 flex items-center gap-3', collapsed ? 'justify-center' : 'px-3')}>
          <LiveIndicator />
        </div>
      </div>
    </aside>
  );
}

/** Tooltip shown on hover when the rail is collapsed to icon-only. */
function RailTooltip({ label }: { label: string }) {
  return (
    <span className="pointer-events-none absolute left-full z-50 ml-3 whitespace-nowrap rounded-md bg-foreground px-2 py-1 text-xs font-medium text-background opacity-0 shadow-md transition-opacity group-hover:opacity-100">
      {label}
    </span>
  );
}

interface RailLinkProps {
  item: { to: string; label: string; Icon: NavItem['Icon'] };
  pathname: string;
  collapsed: boolean;
}

function RailLink({ item, pathname, collapsed }: RailLinkProps) {
  const isActive = pathname === item.to || pathname.startsWith(`${item.to}/`);
  const { Icon } = item;
  return (
    <NavLink
      to={item.to}
      aria-label={item.label}
      className={cn(
        'group relative flex h-[38px] shrink-0 items-center gap-3 rounded-[9px] text-[13.5px] font-medium tracking-tight text-muted-foreground press',
        'hover:bg-sidebar-accent hover:text-sidebar-accent-foreground',
        collapsed ? 'justify-center px-0' : 'px-3',
        isActive && 'bg-primary/10 text-primary hover:bg-primary/10 hover:text-primary',
      )}
    >
      <Icon className="size-[18px] shrink-0" strokeWidth={1.75} />
      {!collapsed && <span className="whitespace-nowrap">{item.label}</span>}
      {item.to === '/today' && <BookingRequestsBadge compact={collapsed} />}
      {isActive && collapsed && (
        <span className="absolute -left-[1px] top-1/2 h-5 w-[2px] -translate-y-1/2 rounded-r bg-primary" />
      )}
      {collapsed && <RailTooltip label={item.label} />}
    </NavLink>
  );
}

function SignOutButton({ collapsed }: { collapsed: boolean }) {
  const navigate = useNavigate();

  async function handleSignOut() {
    await fetch('/api/auth/sign-out', { method: 'POST', credentials: 'include' });
    navigate('/login');
  }

  return (
    <button
      type="button"
      onClick={() => void handleSignOut()}
      aria-label="Sign out"
      className={cn(
        'group relative flex h-[38px] shrink-0 items-center gap-3 rounded-[9px] text-[13.5px] font-medium text-muted-foreground press',
        'hover:bg-sidebar-accent hover:text-sidebar-accent-foreground',
        collapsed ? 'justify-center px-0' : 'px-3',
      )}
    >
      <LogOut className="size-[18px] shrink-0" strokeWidth={1.75} />
      {!collapsed && <span className="whitespace-nowrap">Sign out</span>}
      {collapsed && <RailTooltip label="Sign out" />}
    </button>
  );
}

function MobileTopBar() {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const sections = groupNav(useNav());
  const [open, setOpen] = useState(false);

  // Close on route change.
  useEffect(() => {
    setOpen(false);
  }, [pathname]);

  async function handleSignOut() {
    setOpen(false);
    await fetch('/api/auth/sign-out', { method: 'POST', credentials: 'include' });
    navigate('/login');
  }

  return (
    <>
      <header className="sticky top-0 z-20 flex items-center justify-between border-b border-border bg-background/80 px-4 py-3 backdrop-blur-md md:hidden">
        <div className="flex items-center gap-2">
          <div className="grid size-8 place-items-center rounded-lg bg-primary text-primary-foreground">
            <PawPrint className="size-4" strokeWidth={1.75} />
          </div>
          <span className="text-sm font-medium tracking-tight">Kennel Desk</span>
        </div>
        <button
          type="button"
          onClick={() => setOpen(true)}
          aria-label="Open menu"
          className="grid size-9 place-items-center rounded-lg border border-border bg-surface press"
        >
          <Menu className="size-5" strokeWidth={1.75} />
        </button>
      </header>

      {open && (
        <div
          className="fixed inset-0 z-40 md:hidden"
          role="dialog"
          aria-modal="true"
          aria-label="Navigation"
        >
          <button
            type="button"
            aria-label="Close menu"
            className="absolute inset-0 bg-black/40"
            onClick={() => setOpen(false)}
          />
          <nav className="absolute right-0 top-0 flex h-full w-72 max-w-[85vw] flex-col bg-background shadow-2xl">
            <div className="flex items-center justify-between border-b border-border px-4 py-3">
              <span className="text-sm font-medium">Menu</span>
              <button
                type="button"
                onClick={() => setOpen(false)}
                aria-label="Close menu"
                className="grid size-8 place-items-center rounded-lg hover:bg-secondary press"
              >
                <X className="size-4" strokeWidth={1.75} />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto py-2">
              {/* Primary action */}
              <div className="px-3 pb-2">
                <NavLink
                  to={PRIMARY_ACTION.to}
                  className="flex h-11 items-center gap-3 rounded-[10px] bg-primary px-3 text-sm font-semibold text-primary-foreground press"
                >
                  <PRIMARY_ACTION.Icon className="size-[18px]" strokeWidth={2} />
                  {PRIMARY_ACTION.label}
                </NavLink>
              </div>
              {sections.map((section) => (
                <div key={section.group}>
                  <p className="px-4 pb-1 pt-3 text-[10px] font-semibold uppercase tracking-[0.12em] text-muted-foreground">
                    {section.group}
                  </p>
                  <ul>
                    {section.items.map((item) => {
                      const isActive = pathname === item.to || pathname.startsWith(`${item.to}/`);
                      const { Icon } = item;
                      return (
                        <li key={item.to}>
                          <NavLink
                            to={item.to}
                            className={cn(
                              'flex items-center gap-3 px-4 py-3 text-sm font-medium',
                              isActive
                                ? 'bg-sidebar-accent text-foreground'
                                : 'text-foreground/85 hover:bg-secondary',
                            )}
                          >
                            <Icon className="size-[18px]" strokeWidth={1.75} />
                            {item.label}
                            {item.to === '/today' && <BookingRequestsBadge compact={false} />}
                          </NavLink>
                        </li>
                      );
                    })}
                  </ul>
                </div>
              ))}
              <div className="my-2 h-px bg-border" />
              {FOOTER_NAV.map((item) => {
                const { Icon } = item;
                return (
                  <NavLink
                    key={item.to}
                    to={item.to}
                    className="flex items-center gap-3 px-4 py-3 text-sm font-medium text-foreground/85 hover:bg-secondary"
                  >
                    <Icon className="size-[18px]" strokeWidth={1.75} />
                    {item.label}
                  </NavLink>
                );
              })}
              <WhatsNewButton variant="menu" onOpen={() => setOpen(false)} />
            </div>
            <div className="border-t border-border p-3">
              <button
                type="button"
                onClick={() => void handleSignOut()}
                className="flex w-full items-center gap-3 rounded-md px-3 py-2 text-sm font-medium text-muted-foreground hover:bg-secondary press"
              >
                <LogOut className="size-4" strokeWidth={1.75} />
                Sign out
              </button>
            </div>
          </nav>
        </div>
      )}
    </>
  );
}
